import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { customerProfileApi } from '../services/api';
import { getCurrentPushSubscription, isPushSupported, subscribeToPersonalPush, unsubscribeFromPersonalPush } from '../utils/personalPush';

type NotificationSettings = {
  orders: boolean;
  messages: boolean;
  promotions: boolean;
  priceAlerts: boolean;
  newsletters: boolean;
  sales: boolean;
  marketing: boolean;
  socialFeed: boolean;
  friendConversations: boolean;
  businessConversations: boolean;
  transactionUpdates: boolean;
  digestFrequency: 'realtime' | 'hourly' | 'daily' | 'weekly';
  quietHoursEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  quietHoursTimezone: string;
};

type PrivacySettings = {
  showProfile: boolean;
  allowMessages: boolean;
  showActivity: boolean;
};

const DEFAULT_NOTIFICATIONS: NotificationSettings = {
  orders: true,
  messages: true,
  promotions: true,
  priceAlerts: true,
  newsletters: true,
  sales: true,
  marketing: true,
  socialFeed: true,
  friendConversations: true,
  businessConversations: true,
  transactionUpdates: true,
  digestFrequency: 'realtime',
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '07:00',
  quietHoursTimezone: 'local',
};

const DEFAULT_PRIVACY: PrivacySettings = {
  showProfile: false,
  allowMessages: true,
  showActivity: false,
};

const Settings: React.FC = () => {
  useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [emailConsent, setEmailConsent] = useState(true);
  const [smsConsent, setSmsConsent] = useState(false);
  const [notifications, setNotifications] = useState<NotificationSettings>(DEFAULT_NOTIFICATIONS);
  const [privacy, setPrivacy] = useState<PrivacySettings>(DEFAULT_PRIVACY);
  const [extraNotificationPreferences, setExtraNotificationPreferences] = useState<Record<string, unknown>>({});

  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setMessage(null);
      const res = await customerProfileApi.getPreferences();
      const preferences = (res as { preferences?: { emailConsent?: boolean; smsConsent?: boolean; notificationPreferences?: Record<string, unknown> } })?.preferences;
      const notificationPreferences = preferences?.notificationPreferences || {};

      setEmailConsent(preferences?.emailConsent ?? true);
      setSmsConsent(preferences?.smsConsent ?? false);
      setNotifications({
        orders: Boolean(notificationPreferences.orders ?? DEFAULT_NOTIFICATIONS.orders),
        messages: Boolean(notificationPreferences.messages ?? DEFAULT_NOTIFICATIONS.messages),
        promotions: Boolean(notificationPreferences.promotions ?? DEFAULT_NOTIFICATIONS.promotions),
        priceAlerts: Boolean(notificationPreferences.priceAlerts ?? DEFAULT_NOTIFICATIONS.priceAlerts),
        newsletters: Boolean(notificationPreferences.newsletters ?? DEFAULT_NOTIFICATIONS.newsletters),
        sales: Boolean(notificationPreferences.sales ?? DEFAULT_NOTIFICATIONS.sales),
        marketing: Boolean(notificationPreferences.marketing ?? DEFAULT_NOTIFICATIONS.marketing),
        socialFeed: Boolean(notificationPreferences.socialFeed ?? DEFAULT_NOTIFICATIONS.socialFeed),
        friendConversations: Boolean(notificationPreferences.friendConversations ?? DEFAULT_NOTIFICATIONS.friendConversations),
        businessConversations: Boolean(notificationPreferences.businessConversations ?? DEFAULT_NOTIFICATIONS.businessConversations),
        transactionUpdates: Boolean(notificationPreferences.transactionUpdates ?? DEFAULT_NOTIFICATIONS.transactionUpdates),
        digestFrequency: String(notificationPreferences.digestFrequency ?? DEFAULT_NOTIFICATIONS.digestFrequency) as NotificationSettings['digestFrequency'],
        quietHoursEnabled: Boolean(notificationPreferences.quietHoursEnabled ?? DEFAULT_NOTIFICATIONS.quietHoursEnabled),
        quietHoursStart: String(notificationPreferences.quietHoursStart ?? DEFAULT_NOTIFICATIONS.quietHoursStart),
        quietHoursEnd: String(notificationPreferences.quietHoursEnd ?? DEFAULT_NOTIFICATIONS.quietHoursEnd),
        quietHoursTimezone: String(notificationPreferences.quietHoursTimezone ?? DEFAULT_NOTIFICATIONS.quietHoursTimezone),
      });
      setPrivacy({
        showProfile: Boolean(notificationPreferences.showProfile ?? DEFAULT_PRIVACY.showProfile),
        allowMessages: Boolean(notificationPreferences.allowMessages ?? DEFAULT_PRIVACY.allowMessages),
        showActivity: Boolean(notificationPreferences.showActivity ?? DEFAULT_PRIVACY.showActivity),
      });

      const knownKeys = new Set([
        'orders', 'messages', 'promotions', 'priceAlerts',
        'newsletters', 'sales', 'marketing', 'socialFeed',
        'friendConversations', 'businessConversations', 'transactionUpdates',
        'digestFrequency', 'quietHoursEnabled', 'quietHoursStart', 'quietHoursEnd', 'quietHoursTimezone',
        'showProfile', 'allowMessages', 'showActivity',
      ]);
      const passthrough = Object.fromEntries(
        Object.entries(notificationPreferences).filter(([key]) => !knownKeys.has(key))
      );
      setExtraNotificationPreferences(passthrough);
    } catch (error) {
      console.error('Failed to load settings preferences:', error);
      setMessage('Unable to load remote settings. You can still edit and save.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const updateNotification = (key: keyof NotificationSettings, value: NotificationSettings[keyof NotificationSettings]) => {
    setNotifications((prev) => ({ ...prev, [key]: value }));
    setMessage(null);
  };

  const updatePrivacy = (key: keyof PrivacySettings, value: boolean) => {
    setPrivacy((prev) => ({ ...prev, [key]: value }));
    setMessage(null);
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      setMessage(null);
      await customerProfileApi.updatePreferences({
        emailConsent,
        smsConsent,
        notificationPreferences: {
          ...extraNotificationPreferences,
          ...notifications,
          ...privacy,
        },
      });
      setMessage('Settings saved.');
    } catch (error) {
      console.error('Failed to save settings preferences:', error);
      setMessage('Unable to save right now. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>⚙️ Settings</h1>
      <p style={{ margin: '10px 0 30px 0', color: '#666' }}>Manage your account preferences</p>

      {message && (
        <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', border: '1px solid #fed7aa', background: '#fff7ed', color: '#9a3412' }}>
          {message}
        </div>
      )}
      {loading && (
        <div style={{ marginBottom: '16px', color: '#666' }}>Loading saved settings...</div>
      )}
      
      <div style={{ display: 'grid', gap: '20px', maxWidth: '700px' }}>
        {/* Notifications */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '12px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#333' }}>🔔 Notifications</h3>
          {[
            { label: 'Order updates', key: 'orders' },
            { label: 'Messages', key: 'messages' },
            { label: 'Promotions & deals', key: 'promotions' },
            { label: 'Price alerts', key: 'priceAlerts' },
            { label: 'Newsletters stream', key: 'newsletters' },
            { label: 'Sales stream', key: 'sales' },
            { label: 'Marketing stream', key: 'marketing' },
            { label: 'Social feed stream', key: 'socialFeed' },
            { label: 'Friend conversations stream', key: 'friendConversations' },
            { label: 'Business conversations stream', key: 'businessConversations' },
            { label: 'Transaction stream', key: 'transactionUpdates' },
          ].map((item) => (
            <div key={item.key} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '12px 0',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <span style={{ fontSize: '14px', color: '#333' }}>{item.label}</span>
              <input
                type="checkbox"
                checked={Boolean(notifications[item.key as keyof NotificationSettings])}
                onChange={(e) => updateNotification(item.key as keyof NotificationSettings, e.target.checked)}
              />
            </div>
          ))}

          <div style={{ marginTop: '14px', padding: '12px', border: '1px solid #f0f0f0', borderRadius: '8px', display: 'grid', gap: '10px' }}>
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#333' }}>Digest frequency</span>
              <select
                value={notifications.digestFrequency}
                onChange={(e) => updateNotification('digestFrequency', e.target.value as NotificationSettings['digestFrequency'])}
                style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd' }}
              >
                <option value="realtime">Realtime</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </label>

            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#333' }}>Quiet hours</span>
              <input
                type="checkbox"
                checked={notifications.quietHoursEnabled}
                onChange={(e) => updateNotification('quietHoursEnabled', e.target.checked)}
              />
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '13px', color: '#555' }}>Start</span>
                <input
                  type="time"
                  value={notifications.quietHoursStart}
                  onChange={(e) => updateNotification('quietHoursStart', e.target.value)}
                  disabled={!notifications.quietHoursEnabled}
                  style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '13px', color: '#555' }}>End</span>
                <input
                  type="time"
                  value={notifications.quietHoursEnd}
                  onChange={(e) => updateNotification('quietHoursEnd', e.target.value)}
                  disabled={!notifications.quietHoursEnabled}
                  style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </label>
            </div>
          </div>
          <div style={{ marginTop: '14px', display: 'grid', gap: '10px' }}>
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#333' }}>Email consent</span>
              <input type="checkbox" checked={emailConsent} onChange={(e) => setEmailConsent(e.target.checked)} />
            </label>
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#333' }}>SMS consent</span>
              <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} />
            </label>
          </div>
        </div>

        <EventPhotoPushCard />

        {/* Privacy */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '12px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#333' }}>🔒 Privacy</h3>
          {[
            { label: 'Show my profile publicly', key: 'showProfile' },
            { label: 'Allow messages from other users', key: 'allowMessages' },
            { label: 'Show my activity to connections', key: 'showActivity' },
          ].map((item) => (
            <div key={item.key} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '12px 0',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <span style={{ fontSize: '14px', color: '#333' }}>{item.label}</span>
              <input
                type="checkbox"
                checked={privacy[item.key as keyof PrivacySettings]}
                onChange={(e) => updatePrivacy(item.key as keyof PrivacySettings, e.target.checked)}
              />
            </div>
          ))}
        </div>

        {/* Display */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '12px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#333' }}>🎨 Display</h3>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#333' }}>Theme</label>
            <select style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}>
              <option>System</option>
              <option>Light</option>
              <option>Dark</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#333' }}>Currency</label>
            <select style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}>
              <option>AUD - Australian Dollar</option>
              <option>USD - US Dollar</option>
              <option>EUR - Euro</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '16px' }}>
        <button
          onClick={savePreferences}
          disabled={saving}
          style={{
            padding: '12px 20px',
            background: '#ff6b35',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

/**
 * Separate from the notificationPreferences JSON blob above — this reflects real browser
 * Notification/PushManager state, not a stored preference field. Default-on per group
 * (GroupMember.photoPostingMode defaults to 'prompt'), but the actual push requires the user to
 * grant browser notification permission here at least once.
 */
const EventPhotoPushCard: React.FC = () => {
  const [supported] = useState(isPushSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) return;
    getCurrentPushSubscription().then((sub) => setSubscribed(!!sub)).catch(() => {});
  }, [supported]);

  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      if (subscribed) {
        await unsubscribeFromPersonalPush();
        setSubscribed(false);
      } else {
        await subscribeToPersonalPush();
        setSubscribed(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update notification setting');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: 'white', padding: '25px', borderRadius: '12px' }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#333' }}>📸 Event photo notifications</h3>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '14px', color: '#333' }}>
          Get a push notification when it's time to share photos from a Social Circle event
        </span>
        {supported ? (
          <input type="checkbox" checked={subscribed} disabled={busy} onChange={toggle} />
        ) : (
          <span style={{ fontSize: '12px', color: '#999' }}>Not supported in this browser</span>
        )}
      </div>
      {error && <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#b91c1c' }}>{error}</p>}
      <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#999' }}>
        You'll still see the in-app banner without this — this just makes it proactive.
      </p>
    </div>
  );
};

export default Settings;
